/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/system/PlastronGuardModal.tsx
 *
 * Rôle :
 *   Modale pédagogique globale qui explique à un visiteur connecté en
 *   compte plastron (compte de DÉMONSTRATION en lecture seule) qu'une
 *   action de mutation est désactivée. Affiche :
 *     - un titre clair (« Compte plastron de démonstration »),
 *     - la raison contextuelle (libellé i18n résolu, transmise par
 *       le caller via `usePlastronGuard().requireAction(reason)`),
 *     - un CTA primaire « Se créer un vrai compte » (déconnecte +
 *       navigate /role),
 *     - un CTA secondaire « Continuer en démonstration » (close).
 *
 *   Le store est piloté par `usePlastronGuardStore` (cf. stores/
 *   plastronGuard.ts). La modale est montée UNE SEULE FOIS dans
 *   `<AppLayout>` pour être globalement disponible sur toutes les
 *   routes du layout.
 *
 * Réutilisable :
 *   Composant TNE 2026 spécifique (wording français strict §15.5 +
 *   rôle 'plastron' codé). Pour porter dans un autre projet, adapter
 *   le wording et la cible du CTA primaire.
 *
 * Voir :
 *   - src/stores/plastronGuard.ts (store ouverture/fermeture)
 *   - src/hooks/usePlastronGuard.ts (hook consommateur typique)
 *   - src/components/ui/Modal.tsx (composant Modal partagé §8.6)
 *   - src/components/auth/RequireVisitor.tsx (déconnexion analogue)
 *   - CLAUDE.md §5/§24 2026-05-20.
 *
 * @author Mika
 * @since  2026-05-20
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import { Modal, Button } from '@/components/ui';
import { usePlastronGuardStore } from '@/stores/plastronGuard';
import { VISITOR_SESSION_QUERY_KEY } from '@/hooks/useVisitorSession';
import { apiMutate } from '@/api/client';
// FIX 2026-05-25 (sprint borne, CLAUDE.md §5 entrée 2026-05-25) :
// en mode kiosque (?kiosk=1 + plastron), on masque le bouton « Se
// créer un vrai compte » pour éviter le logout accidentel sur la borne.
import { useKioskMode } from '@/hooks/useKioskMode';

export function PlastronGuardModal() {
  const intl = useIntl();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isOpen = usePlastronGuardStore((s) => s.isOpen);
  const reason = usePlastronGuardStore((s) => s.reason);
  const close = usePlastronGuardStore((s) => s.close);
  const { enabled: isKiosk } = useKioskMode();

  /**
   * « Se créer un vrai compte » :
   *  1. Tente la déconnexion serveur (POST /api/visitor/logout.php).
   *     Tolère un échec réseau — la navigation locale a priorité
   *     (UX : on ne bloque pas l'utilisateur si l'API ne répond pas).
   *  2. Invalide le cache `visitor-session` pour que les gardes
   *     RequireVisitor / HomePage redétectent l'état anonyme.
   *  3. Navigue vers `/role` (page de sélection de profil
   *     d'inscription).
   *  4. Ferme la modale.
   */
  const onCreateRealAccount = useCallback(() => {
    void (async () => {
      try {
        await apiMutate(
          '/visitor/logout.php',
          'POST',
          {},
          { kind: 'visitor-logout', offline: false },
        );
      } catch {
        // Dégradation acceptable : la nav locale suffit même si
        // l'API logout échoue (le cookie TNEVSID expirera côté serveur).
      }
      await qc.invalidateQueries({ queryKey: VISITOR_SESSION_QUERY_KEY });
      close();
      navigate('/role');
    })();
  }, [navigate, qc, close]);

  // Wording de la raison : si vide → fallback générique. Si présent →
  // on l'insère dans le message (template ICU avec placeholder).
  const reasonText = reason.trim();
  const messageId = reasonText !== ''
    ? 'plastron.modal.bodyWithReason'
    : 'plastron.modal.bodyGeneric';

  return (
    <Modal
      open={isOpen}
      onClose={close}
      title={intl.formatMessage({ id: 'plastron.modal.title' })}
      size="md"
      closeOnOverlayClick={true}
    >
      <p>
        <FormattedMessage
          id={messageId}
          values={{ reason: reasonText }}
        />
      </p>
      <p>
        <FormattedMessage id="plastron.modal.cta.help" />
      </p>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          justifyContent: 'flex-end',
          marginTop: '1.5rem',
          flexWrap: 'wrap',
        }}
      >
        <Button variant="secondary" size="md" onClick={close}>
          <FormattedMessage id="plastron.modal.btn.continueDemo" />
        </Button>
        {/* FIX 2026-05-25 (sprint borne) : « Se créer un vrai compte »
            masqué en mode kiosque pour empêcher le logout accidentel
            (la borne doit rester loguée toute la journée). */}
        {!isKiosk && (
          <Button variant="primary" size="md" onClick={onCreateRealAccount}>
            <FormattedMessage id="plastron.modal.btn.createReal" />
          </Button>
        )}
      </div>
    </Modal>
  );
}
